import { ApiV1Job, JobFilterType } from '@bluecrew/crew-member-backend-angular-client';
import dayjs, { Dayjs } from 'dayjs';

/**
 * Filters jobs if they have a filter reason matching the hardcoded shouldFilterReasons. If the function is given
 * a list of filter exemptions, then any job with a filter reason matching the exemptions will not be filtered.
 * @param jobs              A list of ApiV1Jobs to filter.
 * @param filterExemptions  A list of filter reasons that will not cause the job to be filtered out.
 * @returns                 A list of filtered ApiV1Jobs that now excludes any job that had a filter reason within
 *                          the shouldFilterReasons list that was not also in the filterExemptions list.
 */
export const filterJobs = (jobs: ApiV1Job[], filterExemptions: JobFilterType[] = []): ApiV1Job[] => {
    const shouldFilterReasons = [JobFilterType.DoNotSendBack,
                                JobFilterType.DirectInvite,
                                JobFilterType.ScheduleConflict,
                                JobFilterType.FavoritesOnly,
                                JobFilterType.CrewOnly,
                                JobFilterType.MeetsSkillRequirements];
    return jobs.filter(job => filterExemptions.includes(job.primaryFilterReason) || !shouldFilterReasons.includes(job.primaryFilterReason));
};

/**
 * Gets the correctly formatted application-home query params for creating a new job application
 * given a list of externalJobIds
 * @param externalJobIds list of externalJobIds to create the query param string for
 * @returns a single string in the format ?externalId=jobId1&externalIds=jobId1&externalIds=jobId2...
 */
export const getApplicationHomeQueryParamString = (externalJobIds: string[]): string => {
    const externalIdParam = `?externalId=${externalJobIds[0]}`;
    const externalIdsParams = [];
    externalJobIds.forEach(jobId => {
      externalIdsParams.push(`&externalIds=${jobId}`);
    });
    return externalIdParam.concat(externalIdsParams.join(''));
};

/**
 * Takes a list of jobs and aggregates Blueshift jobs, preserving the list order. Blueshift
 * jobs are grouped together by position. Non-blueshift jobs are in singleton lists.
 * @param jobs list of jobs
 * @returns a list of lists of jobs
 */
export const aggregateJobsForBlueshiftAndPosition = (jobs: ApiV1Job[]): ApiV1Job[][] => {
  const blueshiftsAndJobs: ApiV1Job[][] = [];
  const blueshiftIndicesByPosition = {}

  jobs.forEach(job => {
    if (job.blueshiftRequestId) {
      const blueshiftPositionIndex: number = blueshiftIndicesByPosition[job.positionId];

      // if Blueshift with same position already exists in the blueshiftsAndJobs list
      // we need to explictly check for !== undefined because if the first job is a Blueshift job,
      // blueshiftPosition index will be 0, which evaluates to 'false' when we do if (blueshiftPositionIndex)
      // due to JS's truthy/falsey
      if (blueshiftPositionIndex !== undefined && blueshiftPositionIndex < blueshiftsAndJobs.length) {
        blueshiftsAndJobs[blueshiftPositionIndex].push(job);
      } else {
        const length = blueshiftsAndJobs.push(Array.of(job));
        blueshiftIndicesByPosition[job.positionId] = length - 1;
      }
    } else {
      blueshiftsAndJobs.push(Array.of(job));
    }
  })

  return blueshiftsAndJobs;
};

/**
 * Gets the wage range for a given list of jobs
 * @param jobs list of jobs to find the wage range for
 * @returns a tuple containing the minimum and maximum wage
 */
export const getWageRange = (jobs: ApiV1Job[]): [number, number] => {
  let minWage: number = Number.POSITIVE_INFINITY;
  let maxWage: number = Number.NEGATIVE_INFINITY;
  jobs.forEach(job => {
    minWage = job.wage < minWage ? job.wage : minWage;
    maxWage = job.wage > maxWage ? job.wage : maxWage;
  });
  return [minWage, maxWage];
}

/**
 * Gets the first and last date from an array of ApiV1Jobs
 * @param jobs list of jobs to find first and last date from
 * @returns a tuple containing the first and last date of the jobs
 */
export const getStartRange = (jobs: ApiV1Job[]): [Dayjs, Dayjs] => {
  let firstDate: Dayjs = dayjs(jobs[0].startTime);
  let lastDate: Dayjs = dayjs(jobs[0].startTime);
  jobs.forEach(job => {
    const tempStartTime: Dayjs = dayjs(job.startTime);
    firstDate = tempStartTime.isBefore(firstDate) ? tempStartTime : firstDate;
    lastDate = tempStartTime.isAfter(lastDate) ? tempStartTime : lastDate;
  });
  return [firstDate, lastDate];
}
